import { useState, useRef, useCallback, useEffect } from 'react';
import { useNotifQueue } from './useNotifQueue';
import type { FilaJukebox } from '@/shared/types';

export interface UseNotificationToastsReturn {
  nowPlaying:        { titulo: string; thumbnail: string } | null;
  nowPlayingSaindo:  boolean;
  showPedidoPopup:   FilaJukebox | null;
  pedidoPopupSaindo: boolean;
  notifFilaAtiva:    ReturnType<typeof useNotifQueue>['notifAtiva'];
  enfileirarNotif:   ReturnType<typeof useNotifQueue>['enfileirar'];
  mostrarNowPlaying(item: { titulo: string; thumbnail: string }): void;
  mostrarPedidoPopup(item: FilaJukebox): void;
}

/** Notificações visuais da TV: banner "tocando agora" da playlist (NowPlaying)
 *  e o popup de alta prioridade quando um pedido do Jukebox começa a tocar,
 *  além da fila FIFO de notificações menores (useNotifQueue). */
export function useNotificationToasts(): UseNotificationToastsReturn {
  const [nowPlaying,       setNowPlaying]       = useState<{ titulo: string; thumbnail: string } | null>(null);
  const [nowPlayingSaindo, setNowPlayingSaindo] = useState(false);
  const nowPlayingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nowPlayingSaidoRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [showPedidoPopup,   setShowPedidoPopup]   = useState<FilaJukebox | null>(null);
  const [pedidoPopupSaindo, setPedidoPopupSaindo] = useState(false);
  const pedidoPopupSaidoRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const notifQueue = useNotifQueue();

  useEffect(() => () => {
    if (nowPlayingTimerRef.current) clearTimeout(nowPlayingTimerRef.current);
    if (nowPlayingSaidoRef.current) clearTimeout(nowPlayingSaidoRef.current);
    if (pedidoPopupSaidoRef.current) clearTimeout(pedidoPopupSaidoRef.current);
  }, []);

  const mostrarNowPlaying = useCallback((item: { titulo: string; thumbnail: string }) => {
    if (nowPlayingTimerRef.current) clearTimeout(nowPlayingTimerRef.current);
    if (nowPlayingSaidoRef.current) clearTimeout(nowPlayingSaidoRef.current);
    setNowPlayingSaindo(false);
    setNowPlaying(item);
    nowPlayingTimerRef.current = setTimeout(() => {
      setNowPlayingSaindo(true);
      nowPlayingSaidoRef.current = setTimeout(() => {
        setNowPlaying(null);
        setNowPlayingSaindo(false);
      }, 320);
    }, 8_000);
  }, []);

  const mostrarPedidoPopup = useCallback((item: FilaJukebox) => {
    notifQueue.limpar();
    setPedidoPopupSaindo(false);
    setShowPedidoPopup(item);
    if (pedidoPopupSaidoRef.current) clearTimeout(pedidoPopupSaidoRef.current);
    pedidoPopupSaidoRef.current = setTimeout(() => {
      setPedidoPopupSaindo(true);
      pedidoPopupSaidoRef.current = setTimeout(() => {
        setShowPedidoPopup(null);
        setPedidoPopupSaindo(false);
      }, 320);
    }, 6_000);
  }, [notifQueue]);

  return {
    nowPlaying, nowPlayingSaindo,
    showPedidoPopup, pedidoPopupSaindo,
    notifFilaAtiva: notifQueue.notifAtiva,
    enfileirarNotif: notifQueue.enfileirar,
    mostrarNowPlaying, mostrarPedidoPopup,
  };
}
